import { lazy, Suspense, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { initFacebookPixelIfConsented } from "@/lib/fbpixel";
import { onConsentChange } from "@/lib/cookieConsent";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import NotFound from "./pages/NotFound";
import OfferRedeemDialog from "./components/OfferRedeemDialog";
import ErrorBoundary from "./components/ErrorBoundary";
import { MarketingAnalytics } from "./components/MarketingAnalytics";
import ScrollToTop from "./components/ScrollToTop";

const Index = lazy(() => import("./pages/Index"));
const RealitkyWebportal = lazy(() => import("./pages/RealitkyWebportal"));
const RestauracieWebsite = lazy(() => import("./pages/RestauracieWebsite"));
const AdvokatiWebsite = lazy(() => import("./pages/AdvokatiWebsite"));
const ZubnaAmbulanciaWebsite = lazy(() => import("./pages/ZubnaAmbulanciaWebsite"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const Riesenia = lazy(() => import("./pages/Riesenia"));
const RiesenieDetail = lazy(() => import("./pages/RiesenieDetail"));
const Realizacie = lazy(() => import("./pages/Realizacie"));
const RealizacieDetail = lazy(() => import("./pages/RealizacieDetail"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const OchranaSukromia = lazy(() => import("./pages/OchranaSukromia"));
const Vop = lazy(() => import("./pages/Vop"));
const PriebehSpoluprace = lazy(() => import("./pages/PriebehSpoluprace"));
const OSluzbe = lazy(() => import("./pages/OSluzbe"));

// Loads Meta Pixel only once marketing-cookie consent is granted (initial + reactive to a
// mid-session consent change, no reload needed) — see src/lib/fbpixel.ts.
function PixelConsentGate() {
  useEffect(() => {
    initFacebookPixelIfConsented();
    return onConsentChange(() => initFacebookPixelIfConsented());
  }, []);
  return null;
}

function RouteFallback() {
  return (
    <main className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </main>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retryOnMount: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ErrorBoundary>
          <ScrollToTop />
          <PixelConsentGate />
          <MarketingAnalytics />
          <OfferRedeemDialog />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/realitky" element={<RealitkyWebportal />} />
              <Route path="/restauracie" element={<RestauracieWebsite />} />
              <Route path="/advokati" element={<AdvokatiWebsite />} />
              <Route path="/zubna-ambulancia" element={<ZubnaAmbulanciaWebsite />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogArticle />} />
              <Route path="/riesenia" element={<Riesenia />} />
              <Route path="/riesenia/:slug" element={<RiesenieDetail />} />
              <Route path="/realizacie" element={<Realizacie />} />
              <Route path="/realizacie/:slug" element={<RealizacieDetail />} />
              <Route path="/kontakt" element={<Kontakt />} />
              <Route path="/ochrana-sukromia" element={<OchranaSukromia />} />
              <Route path="/obchodne-podmienky" element={<Vop />} />
              <Route path="/priebeh-spoluprace" element={<PriebehSpoluprace />} />
              <Route path="/o-sluzbe" element={<OSluzbe />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
